/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
// Depending on whether rollup is used, luxon needs to be imported differently.
// Since DateTime.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
// TODO(mmalerba): See if we can clean this up at some point.
import { DateTime, DateTimeOptions, Info } from 'luxon';
import { NgxMatDateAdapter } from '@angular-material-components/datetime-picker';

/** Configurable options for {@see DateTimeDateAdapter}. */
export interface NgxMatDateTimeDateAdapterOptions {
  /**
   * When enabled, the dates have to match the format exactly.
   * See https://luxonjs.com/guides/#/parsing/strict-mode/.
   */
  strict?: boolean;

  /**
   * Turns the use of utc dates on or off.
   * Changing this will change how Angular Material components like DatePicker output dates.
   * {@default false}
   */
  useUtc?: boolean;
}

/** InjectionToken for luxon date adapter to configure options. */
export const NGX_MAT_LUXON_DATE_ADAPTER_OPTIONS =
  new InjectionToken<NgxMatDateTimeDateAdapterOptions>(
    'NGX_MAT_LUXON_DATE_ADAPTER_OPTIONS',
    {
      providedIn: 'root',
      factory: NGX_MAT_LUXON_DATE_ADAPTER_OPTIONS_FACTORY,
    },
  );

/** @docs-private */
export function NGX_MAT_LUXON_DATE_ADAPTER_OPTIONS_FACTORY(): NgxMatDateTimeDateAdapterOptions {
  return {
    useUtc: false,
  };
}

/** Creates an array and fills it with values. */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
  const valuesArray = Array(length);
  for (let i = 0; i < length; i++) {
    valuesArray[i] = valueFunction(i);
  }
  return valuesArray;
}

const DEFAULT_DATE_NAMES = range(31, (i) => String(i + 1));

/** Adapts DateTime.js Dates for use with Angular Material. */
@Injectable()
export class NgxMatLuxonAdapter extends NgxMatDateAdapter<DateTime> {
  constructor(
    @Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string,
    @Optional()
    @Inject(NGX_MAT_LUXON_DATE_ADAPTER_OPTIONS)
    private _options?: NgxMatDateTimeDateAdapterOptions,
  ) {
    super();

    this.setLocale(dateLocale || DateTime.now().locale);
  }

  getYear(date: DateTime): number {
    return date.year;
  }

  getMonth(date: DateTime): number {
    return date.month - 1;
  }

  getDate(date: DateTime): number {
    return date.day;
  }

  getDayOfWeek(date: DateTime): number {
    return date.weekday;
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return Info.months(style, { locale: this.locale });
  }

  getDateNames(): string[] {
    if (Intl) {
      // At the time of writing, Luxon doesn't offer similar
      // functionality so we have to fall back to the Intl API.
      const dtf = new Intl.DateTimeFormat(this.locale, {
        day: 'numeric',
        timeZone: 'utc',
      });

      return range(31, (i) => {
        // Format a UTC date in order to avoid DST issues.
        const date = DateTime.utc(2017, 1, i + 1).toJSDate();

        // Strip the directionality characters from the formatted date.
        return dtf.format(date).replace(/[\u200e\u200f]/g, '');
      });
    }

    return DEFAULT_DATE_NAMES;
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const luxonWeekdays = [...Info.weekdays(style, { locale: this.locale })];
    // luxon returns the first day of week as Monday
    // but angular material expects Sunday, so we rotate the array
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    luxonWeekdays.unshift(luxonWeekdays.pop()!);
    return luxonWeekdays;
  }

  getYearName(date: DateTime): string {
    return date.toFormat('yyyy');
  }

  getFirstDayOfWeek(): number {
    return 1;
  }

  getNumDaysInMonth(date: DateTime): number {
    return date.daysInMonth || 0;
  }

  clone(date: DateTime): DateTime {
    return DateTime.fromISO(date.toISO() || '');
  }

  createDate(year: number, month: number, date: number): DateTime {
    // DateTime.js will create an invalid date if any of the components are out of bounds, but we
    // explicitly check each case so we can throw more descriptive errors.
    if (month < 0 || month > 11) {
      throw Error(
        `Invalid month index "${month}". Month index has to be between 0 and 11.`,
      );
    }

    if (date < 1) {
      throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
    }

    const { useUtc }: NgxMatDateTimeDateAdapterOptions = this._options || {};
    const result = useUtc
      ? DateTime.utc(year, month + 1, date)
      : DateTime.local(year, month + 1, date);

    // If the result isn't valid, the date must have been out of bounds for this month.
    if (!result.isValid) {
      throw Error(`Invalid date "${date}" for month with index "${month}".`);
    }

    return result;
  }

  today(): DateTime {
    const { useUtc }: NgxMatDateTimeDateAdapterOptions = this._options || {};

    return (useUtc ? DateTime.utc() : DateTime.local()).setLocale(this.locale);
  }

  parse(value: any, parseFormat: string | string[]): DateTime | null {
    const options: DateTimeOptions = this._getOptions();

    if (value && typeof value == 'string') {
      const iso8601Date = DateTime.fromISO(value, options);
      if (this.isValid(iso8601Date)) {
        return iso8601Date;
      }

      const parseFormats = Array.isArray(parseFormat)
        ? parseFormat
        : [parseFormat];
      for (const format of parseFormats) {
        const fromFormat = DateTime.fromFormat(value, format, options);

        if (this.isValid(fromFormat)) {
          return fromFormat;
        }
      }

      return this.invalid();
    } else if (typeof value === 'number') {
      return DateTime.fromMillis(value, options);
    } else if (value instanceof Date) {
      return DateTime.fromJSDate(value, options);
    } else if (value instanceof DateTime) {
      return DateTime.fromMillis(value.toMillis(), options);
    }

    return null;
  }

  format(date: DateTime, displayFormat: string): string {
    if (!this.isValid(date)) {
      throw Error('DateTimeDateAdapter: Cannot format invalid date.');
    }
    return date.toFormat(displayFormat);
  }

  addCalendarYears(date: DateTime, years: number): DateTime {
    return date.plus({ years });
  }

  addCalendarMonths(date: DateTime, months: number): DateTime {
    return date.plus({ months });
  }

  addCalendarDays(date: DateTime, days: number): DateTime {
    return date.plus({ days });
  }

  toIso8601(date: DateTime): string {
    return date.toString();
  }

  override deserialize(value: any): DateTime | null {
    const options = this._getOptions();
    let date;
    if (value instanceof Date) {
      date = DateTime.fromJSDate(value, options);
    }
    if (typeof value === 'string') {
      if (!value) {
        return null;
      }
      date = DateTime.fromISO(value, options);
    }
    if (date && this.isValid(date)) {
      return date.setLocale(this.locale);
    }
    return super.deserialize(value);
  }

  isDateInstance(obj: any): boolean {
    return obj instanceof DateTime;
  }

  isValid(date: DateTime): boolean {
    return date.isValid;
  }

  invalid(): DateTime {
    return DateTime.invalid('Invalid Luxon DateTime object.');
  }

  getHour(date: DateTime): number {
    return date.hour;
  }

  getMinute(date: DateTime): number {
    return date.minute;
  }

  getSecond(date: DateTime): number {
    return date.second;
  }

  setHour(date: DateTime, value: number): void {
    (date as any)['c'].hour = value;
  }

  setMinute(date: DateTime, value: number): void {
    (date as any)['c'].minute = value;
  }

  setSecond(date: DateTime, value: number): void {
    (date as any)['c'].second = value;
  }

  private _getOptions(): DateTimeOptions {
    const { useUtc }: NgxMatDateTimeDateAdapterOptions = this._options || {};
    return {
      zone: useUtc ? 'utc' : undefined,
      locale: this.locale,
    };
  }
}
